import type {ComponentProps} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

import {Action, css, Icon, Menu, MenuItem, Tooltip} from '@sail/ui';
import {overflow, support} from '@sail/icons/react/Icon';

import {useAnalytics} from '@stripe-internal/conversations-ui';

import {headerMessages} from '../../../core/messages';

const localMessages = defineMessages({
  minimizeTooltip: {
    id: 'conversations_assistant.header.minimizeTooltip',
    description: 'Label for the minimize button',
    defaultMessage: 'Minimize',
  },
  closeTooltip: {
    id: 'conversations_assistant.header.closeTooltip',
    description: 'Label for the close button',
    defaultMessage: 'Close',
  },
});

const messages = {...headerMessages, ...localMessages};

const buttonStyles = css({
  display: 'inline-flex',
  borderRadius: 'rounded',
  padding: 'small',
  ':hover': {
    backgroundColor: 'offset',
  },
});

type Props = {
  showMoreMenu: boolean;
  showSupportCenter: boolean;
  onRestartConversation: () => void;
  onSupportCenterClick: () => void;
  supportCenterUses?: ComponentProps<typeof Action>['uses'];
};

export function HeaderActionsContent({
  showMoreMenu,
  showSupportCenter,
  onRestartConversation,
  onSupportCenterClick,
  supportCenterUses = [],
}: Props) {
  const analytics = useAnalytics();

  return (
    <>
      {showMoreMenu ? (
        <Menu
          trigger={
            <Tooltip
              type="label"
              trigger={
                <Action css={{padding: 'small'}} uses={[buttonStyles]}>
                  <Icon
                    icon={overflow}
                    size="xsmall"
                    data-testid="assistant-header-more-menu"
                  />
                </Action>
              }
            >
              <FormattedMessage {...messages.moreTooltip} />
            </Tooltip>
          }
        >
          <MenuItem
            href="https://docs.stripe.com/"
            target="stripe-docs"
            data-testid="assistant-header-stripe-docs-link"
            onAction={() => {
              analytics.action('stripe_assistant_header_stripe_docs_click');
            }}
          >
            <FormattedMessage {...messages.developerDocs} />
          </MenuItem>
          <MenuItem
            href="https://support.stripe.com/"
            target="stripe-articles"
            data-testid="assistant-header-stripe-articles-link"
            onAction={() => {
              analytics.action('stripe_assistant_header_stripe_articles_click');
            }}
          >
            <FormattedMessage {...messages.supportArticles} />
          </MenuItem>
          <MenuItem
            type="destructive"
            data-testid="assistant-header-stripe-restart-conversation"
            onAction={() => {
              onRestartConversation();
              analytics.action(
                'stripe_assistant_header_stripe_restart_conversation',
              );
            }}
          >
            <FormattedMessage {...messages.restartConversation} />
          </MenuItem>
        </Menu>
      ) : null}

      {showSupportCenter ? (
        <Tooltip
          type="label"
          trigger={
            <Action
              uses={[...supportCenterUses, buttonStyles]}
              onClick={() => {
                analytics.action(
                  'stripe_assistant_header_support_center_click',
                );
                onSupportCenterClick();
              }}
            >
              <Icon
                icon={support}
                size="xsmall"
                data-testid="assistant-header-support-center-link"
              />
            </Action>
          }
        >
          <FormattedMessage {...messages.supportCenter} />
        </Tooltip>
      ) : null}
    </>
  );
}
